import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Package, Trash2, AlertTriangle, Image as ImageIcon, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

async function fileToThumbnail(file, max = 200) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY = { name: "", sku: "", stock: 0, reorder_level: 5, price: 0, image: "" };

export default function Inventory() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const { data: items = [], isLoading } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });

  const openAdd = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ name: it.name || "", sku: it.sku || "", stock: it.stock ?? 0, reorder_level: it.reorder_level ?? 5, price: it.price ?? 0, image: it.image || "" }); setOpen(true); };

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const thumb = await fileToThumbnail(f); setForm((s) => ({ ...s, image: thumb })); }
    catch { toast.error("Couldn't process that image"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    try {
      const payload = { ...form, stock: Number(form.stock), reorder_level: Number(form.reorder_level), price: Number(form.price) };
      if (editId) { await api.put(`/inventory/${editId}`, payload); toast.success("Item updated"); }
      else { await api.post("/inventory", payload); toast.success("Item added"); }
      setOpen(false); setEditId(null); setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/inventory/${id}`); qc.invalidateQueries({ queryKey: ["inventory"] }); };

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Track products, packages, and stock levels." testId="inventory-header" />
      {isLoading ? <PageLoader /> : (
        <div>
          <div className="mb-5 flex justify-end"><Button onClick={openAdd} data-testid="add-inventory-button"><Plus className="mr-2 h-4 w-4" /> Add item</Button></div>
          {items.length === 0 ? (
            <EmptyState icon={Package} title="No inventory items" description="Track products, packages, or service units here." action={<Button onClick={openAdd}><Plus className="mr-2 h-4 w-4" /> Add item</Button>} testId="inventory-empty" />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Price</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((it) => (
                    <tr key={it.id} className="bg-card" data-testid={`inventory-row-${it.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {it.image ? (
                            <button type="button" onClick={() => setPreview(it)} className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary transition-transform hover:scale-105 hover:ring-2 hover:ring-accent/50 focus:outline-none focus:ring-2 focus:ring-accent" title="Preview image" data-testid={`inventory-thumb-button-${it.id}`}>
                              <img src={it.image} alt={it.name} className="h-full w-full object-cover" data-testid={`inventory-thumb-${it.id}`} />
                            </button>
                          ) : (
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <span className="font-medium">{it.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{it.sku}</td>
                      <td className="px-4 py-3">
                        {it.stock <= it.reorder_level ? (
                          <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> {it.stock} low</Badge>
                        ) : <span>{it.stock}</span>}
                      </td>
                      <td className="px-4 py-3">{money(it.price)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => openEdit(it)} data-testid={`edit-inventory-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(it.id)} data-testid={`delete-inventory-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="inventory-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
          {preview?.image && (
            <div className="overflow-hidden rounded-xl border border-border bg-secondary">
              <img src={preview.image} alt={preview.name} className="max-h-[65vh] w-full object-contain" data-testid="inventory-preview-image" />
            </div>
          )}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {preview?.sku && <span>SKU: <span className="font-medium text-foreground">{preview.sku}</span></span>}
            <span>Stock: <span className="font-medium text-foreground">{preview?.stock}</span></span>
            <span>Price: <span className="font-medium text-foreground">{money(preview?.price)}</span></span>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inventory-dialog">
          <DialogHeader><DialogTitle>Add inventory item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary">
                {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" data-testid="inventory-image-preview" /> : <Package className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="inventory-image-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="inventory-image-button"><ImageIcon className="mr-2 h-4 w-4" /> {form.image ? "Change photo" : "Add photo"}</Button>
                {form.image && <Button type="button" variant="ghost" size="sm" onClick={() => { setForm({ ...form, image: "" }); if (fileRef.current) fileRef.current.value = ""; }} data-testid="inventory-image-remove">Remove</Button>}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inventory-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="inventory-sku-input" /></div>
              <div className="space-y-1.5"><Label>Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="inventory-price-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="inventory-stock-input" /></div>
              <div className="space-y-1.5"><Label>Reorder level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="inventory-reorder-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="save-inventory-button">{editId ? "Save changes" : "Add item"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
